/**
 * Analytics Service - Firebase Database Integration
 *
 * Tracks scans, try-ons, and provides aggregated analytics data.
 * Data is organized by date for efficient querying.
 */

import { database } from '../firebase';
import { ref, get } from 'firebase/database';
import { apiCall } from '../api/config';

/**
 * Get today's date string in YYYY-MM-DD format
 */
const getDateString = (date = new Date()) => {
  return date.toISOString().split('T')[0];
};

/**
 * Track a product scan event
 * @param {string} productId - Product ID
 * @param {string} productName - Product name
 * @param {string} method - Scan method ('qr' or 'barcode')
 * @returns {Promise<void>}
 */
export const trackScan = async (productId, productName, method) => {
  try {
    await apiCall('/track', {
      method: 'POST',
      body: JSON.stringify({
        type: 'scan',
        data: { productId, productName, method }
      })
    });
  } catch (error) {
    console.warn('Scan tracking failed (non-fatal):', error.message);
  }
};

/**
 * Track a try-on completion event
 * @param {string} productId - Product ID
 * @param {string} productName - Product name
 * @param {string} contactHash - Customer hash (optional)
 * @returns {Promise<void>}
 */
export const trackTryOn = async (productId, productName, contactHash) => {
  try {
    await apiCall('/track', {
      method: 'POST',
      body: JSON.stringify({
        type: 'tryon',
        data: { productId, productName, contactHash }
      })
    });
  } catch (error) {
    console.warn('TryOn tracking failed (non-fatal):', error.message);
  }
};

/**
 * Get scans for a date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<Array>} Array of scan events
 */
export const getScansForPeriod = async (startDate, endDate) => {
  try {
    const scans = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      const dateStr = getDateString(current);
      const dayRef = ref(database, `analytics/scans/${dateStr}`);
      const snapshot = await get(dayRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.values(data).forEach(scan => {
          scans.push({ ...scan, date: dateStr });
        });
      }

      current.setDate(current.getDate() + 1);
    }

    return scans;
  } catch (error) {
    console.error('Error loading scans:', error);
    return [];
  }
};

/**
 * Get try-ons for a date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<Array>} Array of try-on events
 */
export const getTryOnsForPeriod = async (startDate, endDate) => {
  try {
    const tryons = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      const dateStr = getDateString(current);
      const dayRef = ref(database, `analytics/tryons/${dateStr}`);
      const snapshot = await get(dayRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.values(data).forEach(tryon => {
          tryons.push({ ...tryon, date: dateStr });
        });
      }

      current.setDate(current.getDate() + 1);
    }

    return tryons;
  } catch (error) {
    console.error('Error loading try-ons:', error);
    return [];
  }
};

/**
 * Get top scanned products for the last N days
 * @param {number} days - Number of days to look back
 * @returns {Promise<Array>} Top products sorted by scan count
 */
export const getTopProducts = async (days = 30) => {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const scans = await getScansForPeriod(startDate, endDate);

    // Aggregate by product
    const productCounts = {};
    scans.forEach(scan => {
      const key = scan.productId;
      if (!productCounts[key]) {
        productCounts[key] = {
          productId: scan.productId,
          productName: scan.productName,
          count: 0
        };
      }
      productCounts[key].count++;
    });

    return Object.values(productCounts)
      .sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error loading top products:', error);
    return [];
  }
};

/**
 * Get analytics summary for dashboard
 * @returns {Promise<Object>} Summary with today/week/month counts
 */
export const getAnalyticsSummary = async () => {
  try {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date();
    monthAgo.setDate(monthAgo.getDate() - 30);

    const [scansToday, scansWeek, scansMonth, tryonsToday, tryonsWeek] = await Promise.all([
      getScansForPeriod(today, today),
      getScansForPeriod(weekAgo, today),
      getScansForPeriod(monthAgo, today),
      getTryOnsForPeriod(today, today),
      getTryOnsForPeriod(weekAgo, today)
    ]);

    // Category breakdown from monthly scans
    const categories = {};
    scansMonth.forEach(scan => {
      // We don't have category in scan data, so we group by product
      const name = scan.productName || 'Sconosciuto';
      categories[name] = (categories[name] || 0) + 1;
    });

    return {
      scansToday: scansToday.length,
      scansWeek: scansWeek.length,
      scansMonth: scansMonth.length,
      tryonsToday: tryonsToday.length,
      tryonsWeek: tryonsWeek.length,
      topProducts: await getTopProducts(30),
      categoryCounts: categories
    };
  } catch (error) {
    console.error('Error loading analytics summary:', error);
    return {
      scansToday: 0,
      scansWeek: 0,
      scansMonth: 0,
      tryonsToday: 0,
      tryonsWeek: 0,
      topProducts: [],
      categoryCounts: {}
    };
  }
};
